import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { publicApi } from '../services/api'
import { formatCurrency } from '../config'
import type { SponsorshipSubmission } from '../types'
import LoadingSpinner from '../components/common/LoadingSpinner'

const PROGRAM_NAMES: Record<string, string> = {
  none: 'None',
  coupon: 'Branded Coupon Option Inside Event Program',
  half: 'Branded 1/2 Page in Event Program',
  full: 'Full Branded Page in Event Program',
}

export default function SponsorSuccess() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const [submission, setSubmission] = useState<SponsorshipSubmission | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchSubmission() {
      const submissionId = searchParams.get('submissionId')
      if (!submissionId) {
        setError('No submission ID provided')
        setLoading(false)
        return
      }

      try {
        const response = await publicApi.getSponsorshipSubmission(submissionId)
        setSubmission(response.submission)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load submission')
      } finally {
        setLoading(false)
      }
    }
    fetchSubmission()
  }, [searchParams])

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !submission) {
    return (
      <div className="card text-center py-12">
        <p className="text-red-600 mb-4">{error || 'Submission not found'}</p>
        <a href={`/s/${slug}`} className="btn btn-primary">
          Back to Sponsorship Form
        </a>
      </div>
    )
  }

  const sponsorName = submission.sponsor.displayName ||
    submission.sponsor.businessName ||
    `${submission.sponsor.firstName} ${submission.sponsor.lastName}`

  return (
    <div className="max-w-3xl mx-auto">
      {/* Success Header */}
      <div className="card text-center mb-8">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Thank You for Your Sponsorship!
          </h1>
          <p className="text-xl text-gray-600">
            {sponsorName}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-gray-700">
            A confirmation email/receipt has been sent to <strong>{submission.sponsor.email}</strong>
          </p>
        </div>
      </div>

      {/* Sponsorship Summary */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Sponsorship Summary</h2>

        {/* Sponsor Contact */}
        <div className="mb-6 pb-6 border-b">
          <h3 className="font-semibold text-gray-700 mb-3">Contact Information</h3>
          <div className="grid md:grid-cols-2 gap-2 text-gray-600">
            <p><strong>Name:</strong> {submission.sponsor.firstName} {submission.sponsor.lastName}</p>
            <p><strong>Email:</strong> {submission.sponsor.email}</p>
            <p><strong>Phone:</strong> {submission.sponsor.phone}</p>
            {submission.sponsor.businessName && (
              <p><strong>Business:</strong> {submission.sponsor.businessName}</p>
            )}
          </div>
        </div>

        {/* Hole Sponsorship */}
        {submission.holeSelected && (
          <div className="mb-6 pb-6 border-b">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-sla-green text-white rounded-full text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              Hole Sponsorship - {formatCurrency(submission.holeAmountCents || 0)}
            </h3>
            <div className="ml-8 text-gray-600">
              <p className="mb-2"><strong>Prize Description:</strong> {submission.holePrizeDescription}</p>
              <p className="mb-2"><strong>Estimated Prize Value:</strong> {formatCurrency(submission.holePrizeEstimatedAmountCents || 0)}</p>

              <div className="mt-4">
                <p className="font-medium text-gray-700 mb-2">Included Benefits:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Hole challenge will be at your table and winner will receive your prize</li>
                  <li>Personal 6' Table, 2 Chairs, Table Cloth</li>
                  <li>Branded Flag at Hole</li>
                  <li>Recognition at Awards Ceremony</li>
                  <li>Full Branded Page in Event Program</li>
                  <li>Social Media Thank You Shout Out</li>
                </ul>
              </div>

              {(submission.holeTentRequested || submission.tableRunnerAccepted) && (
                <div className="mt-4">
                  <p className="font-medium text-gray-700 mb-2">Requested Items:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {submission.holeTentRequested && <li>Tent</li>}
                    {submission.tableRunnerAccepted && <li>Branded Table Runner</li>}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Silent Sponsorship */}
        {submission.silentSponsorshipSelected && (
          <div className="mb-6 pb-6 border-b">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-sla-green text-white rounded-full text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              Silent Sponsorship - $500
            </h3>
            <div className="ml-8 text-gray-600">
              <div className="mt-2">
                <p className="font-medium text-gray-700 mb-2">Included Benefits:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Branded Flag at Hole</li>
                  <li>Recognition at Awards Ceremony</li>
                  <li>Full Branded Page in Event Program</li>
                  <li>Social Media Thank You Shout Out</li>
                </ul>
              </div>

              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  Your hole assignment will be handled by the event organizers.
                </p>
              </div>

              {/* Logo Status */}
              <div className="mt-4">
                {submission.silentAuctionLogoUrl ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Logo uploaded successfully
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Logo not uploaded:</strong> Please email your logo to{' '}
                      <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
                        secretary@schoollunchangels.org
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Silent Auction */}
        {submission.silentAuctionSelected && (
          <div className="mb-6 pb-6 border-b">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-sla-green text-white rounded-full text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              Silent Auction Contribution
            </h3>
            <div className="ml-8 text-gray-600">
              <p className="mb-2"><strong>Prize Description:</strong> {submission.silentAuctionPrizeDescription}</p>
              <p className="mb-2"><strong>Prize Value:</strong> {formatCurrency(submission.silentAuctionPrizeAmountCents || 0)}</p>

              <div className="mt-4">
                <p className="font-medium text-gray-700 mb-2">Included Benefits:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Logo and Recognition on Auction Contribution Page in Event Program</li>
                </ul>
              </div>

              {/* Logo Status */}
              <div className="mt-4">
                {submission.silentAuctionLogoUrl ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Logo uploaded successfully
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Logo not uploaded:</strong> Please email your logo to{' '}
                      <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
                        secretary@schoollunchangels.org
                      </a>
                    </p>
                  </div>
                )}
              </div>

              {/* Pickup Info Status */}
              <div className="mt-4">
                {submission.silentAuctionPrizePickupInfo ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-sm text-gray-700">
                      <strong>Prize Pickup:</strong> {submission.silentAuctionPrizePickupInfo}
                    </p>
                    <p className="text-sm text-orange-600 mt-1 font-medium">
                      March 31st is the deadline for prize pickups.
                    </p>
                  </div>
                ) : (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-orange-700">
                      <strong>Action Required:</strong> Please contact us about prize pickup arrangements at{' '}
                      <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
                        secretary@schoollunchangels.org
                      </a>
                    </p>
                    <p className="text-sm text-orange-600 mt-1 font-medium">
                      March 31st is the deadline for prize pickups.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Golf Foursome */}
        {submission.golfFoursomeSelected && (
          <div className="mb-6 pb-6 border-b">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-sla-green text-white rounded-full text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              Golf Foursome Registration - {formatCurrency(submission.golfFoursomeAmountCents || 60000)}
            </h3>
            <div className="ml-8 text-gray-600">
              {submission.couponCode && submission.couponDiscountCents && submission.couponDiscountCents > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Coupon Applied: {submission.couponCode} (-{formatCurrency(submission.couponDiscountCents)})
                  </p>
                </div>
              )}
              <p className="text-sm mb-3">4 golfer registrations for the tournament</p>

              {submission.golfFoursomeGolfers && submission.golfFoursomeGolfers.length > 0 && (
                <div className="mt-4">
                  <p className="font-medium text-gray-700 mb-2">Registered Golfers:</p>
                  <div className="space-y-2">
                    {submission.golfFoursomeGolfers.map((golfer, index) => (
                      <div key={index} className="bg-gray-50 rounded p-2 text-sm">
                        <span className="font-medium">Golfer {index + 1}:</span>{' '}
                        {golfer.name || <span className="text-gray-400 italic">Not provided</span>}
                        {golfer.email && <span className="text-gray-500 ml-2">({golfer.email})</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!submission.golfFoursomeGolfers || submission.golfFoursomeGolfers.every(g => !g.name)) && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-700">
                    <strong>Golfer details not provided:</strong> Please contact us at{' '}
                    <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
                      secretary@schoollunchangels.org
                    </a>{' '}
                    to provide golfer names and contact information.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Program Advertising */}
        {submission.programOption !== 'none' && (
          <div className="mb-6 pb-6 border-b">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-sla-green text-white rounded-full text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
              {PROGRAM_NAMES[submission.programOption]}
            </h3>
            <div className="ml-8 text-gray-600">
              <p className="text-sm">Event program advertising space</p>
            </div>
          </div>
        )}

        {/* Referral Source */}
        {submission.referralSource && (
          <div className="mb-6 pb-6 border-b">
            <p className="text-gray-600">
              <strong>How you heard about us:</strong>{' '}
              {submission.referralSource === 'Other' && submission.referralSourceOther
                ? `Other - ${submission.referralSourceOther}`
                : submission.referralSource}
            </p>
          </div>
        )}

        {/* Payment Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-700 mb-4">Payment Summary</h3>

          {submission.couponCode && submission.couponDiscountCents && submission.couponDiscountCents > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Coupon ({submission.couponCode}):</span>
              <span className="font-semibold text-green-600">-{formatCurrency(submission.couponDiscountCents)}</span>
            </div>
          )}

          {submission.amountDueCents > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Amount Paid:</span>
              <span className="font-semibold text-green-600">{formatCurrency(submission.amountDueCents)}</span>
            </div>
          )}

          {submission.inKindTotalCents > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">In-Kind Contribution Value:</span>
              <span className="font-semibold text-gray-700">{formatCurrency(submission.inKindTotalCents)}</span>
            </div>
          )}

          <div className="mt-4 pt-4 border-t text-sm text-gray-600">
            <p className="mb-1">
              <strong>Status:</strong>{' '}
              <span className={
                submission.status === 'paid' ? 'text-green-600' :
                submission.status === 'no_payment_required' ? 'text-blue-600' :
                'text-yellow-600'
              }>
                {submission.status === 'paid' && 'Paid'}
                {submission.status === 'no_payment_required' && 'Confirmed (No Payment Required)'}
                {submission.status === 'pending' && 'Pending Payment'}
              </span>
            </p>
            <p><strong>Confirmation ID:</strong> {submission.submissionId}</p>
          </div>
        </div>
      </div>

      {/* Tax Receipt Info */}
      <div className="card mb-8">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold text-gray-800">Tax Deductible Receipt</h2>
          <button
            onClick={() => window.print()}
            className="btn btn-secondary flex items-center gap-2 text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
        </div>
        <p className="text-gray-600 mb-4">
          School Lunch Angels is a 501(c)(3) non-profit organization. Your contribution may be tax-deductible.
        </p>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-700"><strong>Organization:</strong> School Lunch Angels</p>
          <p className="text-gray-700"><strong>EIN:</strong> 33-5040677</p>
          {submission.amountDueCents > 0 && (
            <p className="text-gray-700"><strong>Paid Amount:</strong> {formatCurrency(submission.amountDueCents)}</p>
          )}
          {submission.inKindTotalCents > 0 && (
            <p className="text-gray-700"><strong>In-Kind Contribution:</strong> {formatCurrency(submission.inKindTotalCents)}</p>
          )}
          <p className="text-gray-700"><strong>Date:</strong> {new Date(submission.createdAt).toLocaleDateString()}</p>
        </div>
        <p className="text-sm text-gray-500 mt-4">
          Please consult your tax advisor regarding the deductibility of your contribution.
        </p>
      </div>

      {/* Event Details Reminder */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Event Details</h2>
        <div className="grid md:grid-cols-2 gap-4 text-gray-700">
          <div>
            <p><strong>Date:</strong> April 11, 2026</p>
            <p><strong>Location:</strong> Callaway Resort & Gardens</p>
            <p className="text-gray-600 ml-4">Lake View Golf Course</p>
            <p className="text-gray-600 ml-4">3396 Golf House Drive, Pine Mountain, GA 31822</p>
          </div>
          <div>
            <p><strong>Check In Time:</strong> 11:00 AM</p>
            <p><strong>Shotgun Start:</strong> 12:00 PM</p>
            <p><strong>Awards Ceremony:</strong> Following Tournament</p>
          </div>
        </div>
      </div>

      {/* Contact Info */}
      <div className="card text-center">
        <p className="text-gray-600 mb-4">
          Questions? Contact us at{' '}
          <a href="mailto:secretary@schoollunchangels.org" className="text-sla-blue hover:underline">
            secretary@schoollunchangels.org
          </a>
        </p>
        <p className="text-gray-500 text-sm">You may close this window.</p>
      </div>
    </div>
  )
}
